/* Copyright (c) 2020-2021 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { Container } from "react-awesome-styled-grid";
import { useParams } from "react-router-dom";
import { Box, Loader, Spacer } from "@nordcloud/gnui";
import styles from "~/assets/styles/Runbook.module.css";
import { ApplicationInitialState } from "~/components/Applications/ApplicationDetails/initialState";
import { Customer } from "~/components/BasicCustomerInfo/types";
import { useShowError } from "~/components/Error/useShowError";

import { ApplicationDetails } from "~/components/Runbook/ApplicationDetails";
import { AutoBackupConfig } from "~/components/Runbook/AutoBackupConfig";
import { AutoPatcherPlans } from "~/components/Runbook/AutoPatcherPlans";
import { CloudAccounts } from "~/components/Runbook/CloudAccounts";
import { CustomerDetails } from "~/components/Runbook/CustomerDetails";
import { Environments } from "~/components/Runbook/Environments";
import { FleetControlPatchingPlans } from "~/components/Runbook/FleetControlPatchingPlans";
import { NordcloudServices } from "~/components/Runbook/NordcloudServices/NordcloudServices";
import { PagerDutyServices } from "~/components/Runbook/PagerDutyServices";

import { SaveRunbook } from "~/components/Runbook/SaveRunbook";
import { Divider } from "~/components/Styled/Divider";
import { CustomerInitialState, useCustomer } from "~/context/User/Customer";
import { Application, ApplicationRouteParams } from "~/models";
import { useAxios } from "~/tools";

function Runbook() {
  const { customer: customerInitialState, isCustomerLoading } = useCustomer();
  const [isLoading, setIsLoading] = React.useState(true);

  const { showError } = useShowError();

  const { Axios } = useAxios();

  const routeParams = useParams<ApplicationRouteParams>();
  const { applicationId } = routeParams;

  const [application, setApplication] = React.useState<Application>(
    ApplicationInitialState
  );

  const [customer, setCustomer] = React.useState<Customer>(
    CustomerInitialState
  );

  const fetchApplication = React.useCallback(async () => {
    try {
      const { data } = await Axios().get(`/applications/${applicationId}`);
      setApplication(data);
    } catch (e) {
      showError(e.response);
    } finally {
      setIsLoading(false);
    }
  }, [Axios, applicationId, showError]);

  React.useEffect(() => {
    fetchApplication();
  }, [fetchApplication]);

  React.useEffect(() => {
    !isCustomerLoading && setCustomer(customerInitialState);
  }, [customerInitialState, isCustomerLoading]);

  if (isLoading || isCustomerLoading) {
    return (
      <Container css={{ width: "1200px" }}>
        <Loader inContent />
      </Container>
    );
  }

  return (
    <Container css={{ width: "1200px" }}>
      <Box shadow="shadow00" innerSpacing="spacing03">
        <Divider>
          {application.runbook ? (
            <div className={styles.title}>
              <a
                href={application.runbook.url}
                target="_blank"
                rel="noreferrer"
              >
                {application.runbook.name}
              </a>
            </div>
          ) : (
            <div className={styles.title}>
              {customer?.name} - {application?.name} Runbook
            </div>
          )}
        </Divider>

        <CustomerDetails customer={customer} />

        <ApplicationDetails application={application} />

        <CloudAccounts application={application} />

        <Environments application={application} />

        <PagerDutyServices application={application} />

        <AutoBackupConfig application={application} />

        <AutoPatcherPlans application={application} />

        <FleetControlPatchingPlans application={application} />

        <NordcloudServices application={application} />

        <Spacer height="1rem" />
        <SaveRunbook application={application} refetch={fetchApplication} />
      </Box>
    </Container>
  );
}

export { Runbook };
